// src/lib/themes.js
// FamFlow premium theme system.
// Strategy preserved from prior version: scope overrides with [data-theme="key"]
// over the existing Tailwind utility classes the UI already uses (.bg-white,
// .bg-neutral-100, .border, .text-neutral-*), plus CSS variables consumed by a
// few inline styles in App.jsx (--ff-text, --ff-muted, --ff-divider,
// --ff-sidebar-bg, --ff-sidebar-border). Only the ACTIVE theme's css is injected.
//
// Stored keys (backward compatible):
//   light        -> Soft Light  (default; improved in place, no migration)
//   dark         -> Night Mode  (redesigned; existing "dark" users upgrade cleanly)
//   scenic       -> Scenic      (new)
//   celebration  -> Celebration (new)
//   modern/clean/space -> hidden legacy aliases -> map to a designed theme so any
//                          previously-saved user still lands on a polished look.

// ---- shared helpers -------------------------------------------------------

// Pastel member-tint classes used across the app for member areas.
const PASTELS = ['bg-blue-200','bg-pink-200','bg-emerald-200','bg-orange-200','bg-purple-200','bg-teal-200','bg-indigo-200','bg-red-200','bg-yellow-100'];

// Keep member-tinted areas readable on dark surfaces: force dark text inside the
// pastel tint (identity accent stays the color, text stays legible), while
// buttons and completed states keep their own contrast.
const darkPastelText = (t) => {
  const inside      = PASTELS.map(c=>`[data-theme="${t}"] .${c} *`).join(',');
  const btns        = PASTELS.map(c=>`[data-theme="${t}"] .${c} button:not(.bg-green-600)`).join(',');
  const btnChildren = PASTELS.map(c=>`[data-theme="${t}"] .${c} button:not(.bg-green-600) *`).join(',');
  return `
    ${inside}{color:#1f2937!important}
    ${btns},${btns} span{color:#f1f5f9!important}
    ${btnChildren}{color:#f1f5f9!important}
  `;
};

// ---- Soft Light (key: light) ---------------------------------------------
const SOFT = `
  [data-theme="light"]{
    --ff-text:#1f2937;--ff-muted:#6b7280;--ff-divider:#e6eaf2;
    --ff-sidebar-bg:rgba(255,255,255,.92);--ff-sidebar-border:#e6eaf2;
    background-color:#f4f6fb!important;
  }
  [data-theme="light"].ff-main{background-color:#f4f6fb!important}
  [data-theme="light"] .bg-neutral-100{background-color:#f4f6fb!important}
  [data-theme="light"] .bg-white{
    background-color:#ffffff!important;border-color:#eaeef5!important;
    box-shadow:0 1px 2px rgba(16,24,40,.05),0 4px 14px rgba(16,24,40,.05)!important;
  }
  [data-theme="light"] .bg-neutral-50{background-color:#f6f8fc!important}
  [data-theme="light"] .bg-neutral-200{background-color:#e6eaf2!important}
  [data-theme="light"] .border{border-color:#eaeef5!important}
  [data-theme="light"] .shadow{box-shadow:0 1px 2px rgba(16,24,40,.05),0 4px 14px rgba(16,24,40,.05)!important}
  [data-theme="light"] .ff-sidebar{background:var(--ff-sidebar-bg)!important;border-right:1px solid var(--ff-sidebar-border)!important}
  [data-theme="light"] .chore-btn-incomplete{background:#ffffff!important;color:#1f2937!important;border:1px solid #eaeef5!important;box-shadow:0 1px 2px rgba(16,24,40,.04)!important}
  [data-theme="light"] .chore-btn-incomplete *{color:#1f2937!important}
  [data-theme="light"] .chore-btn-incomplete .chore-pts{color:#2563eb!important}
`;

// ---- Night Mode (key: dark) ----------------------------------------------
const NIGHT = `
  [data-theme="dark"]{
    --ff-text:#e7ecf5;--ff-muted:#93a2bd;--ff-divider:#2a3852;
    --ff-sidebar-bg:rgba(16,23,41,.94);--ff-sidebar-border:#25324b;
    background-color:#0f1729!important;
  }
  [data-theme="dark"].ff-main{background-color:#0f1729!important}
  [data-theme="dark"] .bg-neutral-100{background-color:#0f1729!important}
  [data-theme="dark"] .bg-white{
    background-color:#1a2438!important;border-color:#2a3852!important;
    box-shadow:0 1px 3px rgba(0,0,0,.5),0 6px 18px rgba(0,0,0,.35)!important;
  }
  [data-theme="dark"] .bg-neutral-50{background-color:#212d44!important}
  [data-theme="dark"] .bg-neutral-200{background-color:#2a3852!important}
  [data-theme="dark"] .border{border-color:#2a3852!important}
  [data-theme="dark"] .shadow{box-shadow:0 1px 3px rgba(0,0,0,.5),0 6px 18px rgba(0,0,0,.35)!important}
  [data-theme="dark"] .ff-sidebar{background:var(--ff-sidebar-bg)!important;border-right:1px solid var(--ff-sidebar-border)!important}
  [data-theme="dark"] button.bg-white{background-color:#212d44!important}
  [data-theme="dark"] button.bg-white:hover{background-color:#28374f!important}
  [data-theme="dark"] input,[data-theme="dark"] select,[data-theme="dark"] textarea{background-color:#212d44!important;border-color:#2a3852!important;color:#e7ecf5!important}
  [data-theme="dark"] .bg-white *{color:#e7ecf5!important}
  [data-theme="dark"] label,[data-theme="dark"] .text-neutral-700,[data-theme="dark"] .text-neutral-800,[data-theme="dark"] .text-neutral-900{color:#e7ecf5!important}
  [data-theme="dark"] .text-neutral-400,[data-theme="dark"] .text-neutral-500,[data-theme="dark"] .text-neutral-600{color:#93a2bd!important}
  [data-theme="dark"] .text-red-500,[data-theme="dark"] .text-red-600,[data-theme="dark"] .text-red-700{color:#f87171!important}
  [data-theme="dark"] .text-green-600,[data-theme="dark"] .text-green-700{color:#34d399!important}
  [data-theme="dark"] .text-blue-600,[data-theme="dark"] .text-blue-700{color:#60a5fa!important}
  [data-theme="dark"] .text-amber-600,[data-theme="dark"] .text-yellow-700{color:#fbbf24!important}
  [data-theme="dark"] .bg-red-50{background:rgba(239,68,68,.16)!important}
  [data-theme="dark"] .bg-yellow-50{background:rgba(234,179,8,.14)!important}
  [data-theme="dark"] .bg-green-50{background:rgba(34,197,94,.14)!important}
  [data-theme="dark"] .bg-blue-50{background:rgba(59,130,246,.16)!important}
  [data-theme="dark"] .bg-green-600,[data-theme="dark"] .bg-green-600 *{color:#fff!important}
  [data-theme="dark"] .chore-btn-incomplete{background:#25324b!important;border:1px solid #33425f!important}
  [data-theme="dark"] .chore-btn-incomplete *{color:#e7ecf5!important}
  [data-theme="dark"] .chore-btn-incomplete .chore-pts{color:#93a2bd!important}
  ${darkPastelText('dark')}
`;

// ---- Scenic (key: scenic) -------------------------------------------------
// Light, frosted-glass panels over a subdued sunrise landscape. Panels stay
// light so text remains dark and legible. Falls back to a sky gradient if the
// image asset is missing, so it never looks broken.
const SCENIC = `
  [data-theme="scenic"]{
    --ff-text:#24303f;--ff-muted:#5b6b7f;--ff-divider:rgba(90,110,130,.28);
    --ff-sidebar-bg:rgba(255,255,255,.80);--ff-sidebar-border:rgba(255,255,255,.6);
    background-color:#cfe0ec!important;
    background-image:
      linear-gradient(rgba(244,248,252,.34),rgba(226,236,244,.5)),
      url('/images/themes/scenic-bg.svg'),
      linear-gradient(180deg,#dcecf6 0%,#e8eecf 55%,#f3e6d2 100%)!important;
    background-size:cover!important;background-position:center!important;
    background-attachment:fixed!important;background-repeat:no-repeat!important;
  }
  [data-theme="scenic"].ff-main{background:transparent!important}
  [data-theme="scenic"] .bg-neutral-100{background:transparent!important}
  [data-theme="scenic"] .bg-white{
    background:rgba(255,255,255,.80)!important;border-color:rgba(255,255,255,.65)!important;
    backdrop-filter:blur(10px) saturate(1.05);-webkit-backdrop-filter:blur(10px) saturate(1.05);
    box-shadow:0 2px 10px rgba(40,60,80,.12),0 8px 26px rgba(40,60,80,.10)!important;
  }
  [data-theme="scenic"] .bg-neutral-50{background:rgba(255,255,255,.55)!important}
  [data-theme="scenic"] .bg-neutral-200{background:rgba(255,255,255,.6)!important}
  [data-theme="scenic"] .border{border-color:rgba(255,255,255,.6)!important}
  [data-theme="scenic"] .shadow{box-shadow:0 2px 10px rgba(40,60,80,.12)!important}
  [data-theme="scenic"] .ff-sidebar{background:var(--ff-sidebar-bg)!important;border-right:1px solid var(--ff-sidebar-border)!important;backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px)}
  [data-theme="scenic"] input,[data-theme="scenic"] select,[data-theme="scenic"] textarea{background:rgba(255,255,255,.85)!important}
  [data-theme="scenic"] .chore-btn-incomplete{background:rgba(255,255,255,.78)!important;color:#24303f!important;border:1px solid rgba(255,255,255,.7)!important}
  [data-theme="scenic"] .chore-btn-incomplete *{color:#24303f!important}
  [data-theme="scenic"] .chore-btn-incomplete .chore-pts{color:#1d6fb8!important}
`;

// ---- Celebration (key: celebration) --------------------------------------
// Clean, bright, FamFlow-blue forward with restrained violet/coral/gold accents
// concentrated on success states (completed chores, progress, counters).
const CELEBRATION = `
  [data-theme="celebration"]{
    --ff-text:#1f2430;--ff-muted:#6b7280;--ff-divider:#eae7f4;
    --ff-sidebar-bg:rgba(255,255,255,.94);--ff-sidebar-border:#ece9f6;
    background-color:#f7f8fc!important;
    background-image:radial-gradient(circle at 12% 8%,rgba(124,58,237,.06),transparent 42%),radial-gradient(circle at 88% 6%,rgba(251,146,60,.06),transparent 40%)!important;
  }
  [data-theme="celebration"].ff-main{background:transparent!important}
  [data-theme="celebration"] .bg-neutral-100{background:transparent!important}
  [data-theme="celebration"] .bg-white{
    background-color:#ffffff!important;border-color:#eceaf6!important;
    box-shadow:0 1px 2px rgba(79,70,229,.06),0 6px 18px rgba(79,70,229,.07)!important;
  }
  [data-theme="celebration"] .bg-neutral-50{background-color:#f6f5fc!important}
  [data-theme="celebration"] .bg-neutral-200{background-color:#ece9f6!important}
  [data-theme="celebration"] .border{border-color:#eceaf6!important}
  [data-theme="celebration"] .shadow{box-shadow:0 1px 2px rgba(79,70,229,.06),0 6px 18px rgba(79,70,229,.07)!important}
  [data-theme="celebration"] .ff-sidebar{background:var(--ff-sidebar-bg)!important;border-right:1px solid var(--ff-sidebar-border)!important}
  [data-theme="celebration"] .chore-btn-incomplete{background:#ffffff!important;color:#1f2430!important;border:1px solid #eceaf6!important;box-shadow:0 1px 2px rgba(79,70,229,.05)!important}
  [data-theme="celebration"] .chore-btn-incomplete *{color:#1f2430!important}
  [data-theme="celebration"] .chore-btn-incomplete .chore-pts{color:#7c3aed!important}
  /* success accents: gold-lift completed chores, warmer green */
  [data-theme="celebration"] .bg-green-600{background:linear-gradient(135deg,#16a34a,#15b8a6)!important;box-shadow:0 2px 10px rgba(16,163,74,.28),0 0 0 1px rgba(250,204,21,.35)!important}
  [data-theme="celebration"] .bg-green-600,[data-theme="celebration"] .bg-green-600 *{color:#fff!important}
`;

// ---- exports --------------------------------------------------------------
export const THEMES = {
  // Visible premium themes (order = selector order)
  light:       { name:'Soft Light',  swatch:{ bg:'#f4f6fb', card:'#ffffff', a1:'#2563eb', a2:'#93c5fd' }, css:SOFT },
  dark:        { name:'Night Mode',  swatch:{ bg:'#0f1729', card:'#1a2438', a1:'#60a5fa', a2:'#34d399' }, css:NIGHT },
  scenic:      { name:'Scenic',      swatch:{ bg:'#dcecf6', card:'#ffffff', a1:'#1d6fb8', a2:'#e8eecf' }, css:SCENIC },
  celebration: { name:'Celebration', swatch:{ bg:'#f7f8fc', card:'#ffffff', a1:'#2563eb', a2:'#7c3aed' }, css:CELEBRATION },

  // Hidden legacy aliases — keep previously-saved users on a designed theme.
  modern: { name:'Night Mode',  hidden:true, css:NIGHT.replace(/dark"\]/g,'modern"]') },
  clean:  { name:'Soft Light',  hidden:true, css:SOFT.replace(/light"\]/g,'clean"]') },
  space:  { name:'Night Mode',  hidden:true, css:NIGHT.replace(/dark"\]/g,'space"]') },
};
